import { createTheme } from '@mui/material/styles';
import { tokens } from './tokens';
import { heidrickComponents } from './components';

// IMPORTANT: import the augmentation file so its `declare module` blocks are
// included in the build. Type-only side effect.
import type {} from './mui-augment';

/*
 * heidrickTheme — MUI theme object for the heidrick design system instance.
 *
 * Sourced from Tier 1 raw tokens (src/styles/tokens-raw.css) via the build step
 * (scripts/generate-theme-tokens.mjs). MUI 5.10.6-compatible; uses createTheme
 * (NOT Experimental_CssVarsProvider — that's 5.13+ and not available on the
 * AssessorWorkFlowFE target version).
 *
 * Palette mapping (Tier 1 hex → MUI semantic role):
 *   primary       → darker-blue (#002136) — main Heidrick brand
 *   secondary     → teal (#197E95) — Heidrick accent
 *   text.primary  → rich-black
 *   text.secondary → gray-1
 *   background    → white surface
 *   grey scale    → Heidrick's gray-1 through gray-6
 *
 * Per-component overrides (Button + future) live in ./components.ts and
 * inline resolved hex values directly — palette IS used by consumers writing
 * MUI-native code (sx, styled), but Button's variant entries pull tokens
 * directly to keep the variant matrix self-contained.
 */
export const heidrickTheme = createTheme({
  palette: {
    mode: 'light',
    common: {
      black: tokens.color['rich-black'],
      white: tokens.color['white'],
    },
    primary: {
      main: tokens.color['darker-blue'],
      contrastText: tokens.color['white'],
    },
    secondary: {
      main: tokens.color['teal'],
      contrastText: tokens.color['white'],
    },
    text: {
      primary: tokens.color['rich-black'],
      secondary: tokens.color['gray-1'],
      disabled: tokens.color['gray-3'],
    },
    background: {
      default: tokens.color['white'],
      paper: tokens.color['white'],
    },
    grey: {
      // Heidrick gray scale — values are intentionally NOT MUI's defaults
      50: tokens.color['gray-6'],
      100: tokens.color['gray-5'],
      200: tokens.color['gray-4'],
      300: tokens.color['gray-3-5'],
      400: tokens.color['gray-3'],
      500: tokens.color['gray-2'],
      600: tokens.color['gray-1'],
      700: tokens.color['dark-blue'],
      800: tokens.color['middle-blue'],
      900: tokens.color['rich-black'],
    },
    error: {
      // No dedicated error color in the Heidrick base palette yet; using magenta-bright
      // as the closest sanctioned brand alarm color. Replace if a real error color lands.
      main: tokens.color['magenta-bright'],
    },
  },
  typography: {
    fontFamily: tokens.font['family-avenir'],
    button: {
      // Per Figma: button text doesn't uppercase
      textTransform: 'none',
      fontWeight: 400,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: tokens.breakpoint.sm,
      md: tokens.breakpoint.md,
      lg: tokens.breakpoint.lg,
      xl: tokens.breakpoint.xl,
    },
  },
  shape: {
    borderRadius: 4, // MUI default; per-primitive border-radius lives in component overrides
  },
  components: heidrickComponents,
});

export { tokens } from './tokens';
export { heidrickComponents } from './components';
export type { Tokens } from './tokens';
